import { FC, useMemo } from "react"
import { TextFieldElement, useWatch } from "react-hook-form-mui"
import { Stack, Typography } from "@mui/material"
import { OSINT } from "../../interfaces"
import { QUERY_TYPE_BY_KEY } from "../../constants"

interface IProps {
  name?: string
  typeFieldName?: string
  required?: boolean
  disabled?: boolean
}

export interface ParsedBulk {
  /** Невалидные строки: индекс (1-based) и сама строка */
  invalid: { line: number; value: string }[]
  /** Валидные payloads — готовы к отправке */
  payloads: OSINT.Payload[]
  /** Всего непустых строк */
  total: number
}

export const parseBulkInput = (
  raw: string,
  type: OSINT.PayloadType | undefined,
): ParsedBulk => {
  if (!type) return { invalid: [], payloads: [], total: 0 }
  const meta = QUERY_TYPE_BY_KEY[type]
  if (!meta) return { invalid: [], payloads: [], total: 0 }

  const lines = raw.split(/\r?\n/)
  const invalid: ParsedBulk["invalid"] = []
  const payloads: OSINT.Payload[] = []
  let total = 0

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim()
    if (!line) return
    total++
    if (meta.validate(line)) {
      payloads.push(meta.parseLine(line))
    } else {
      invalid.push({ line: idx + 1, value: line })
    }
  })

  return { invalid, payloads, total }
}

export const BulkInputField: FC<IProps> = ({
  name = "bulkInput",
  typeFieldName = "queryType",
  required,
  disabled,
}) => {
  const type = useWatch({ name: typeFieldName }) as
    | OSINT.PayloadType
    | undefined
  const value = useWatch({ name }) as string | undefined

  const meta = type ? QUERY_TYPE_BY_KEY[type] : undefined
  const parsed = useMemo(() => parseBulkInput(value ?? "", type), [value, type])

  const helperText = meta
    ? `${meta.inputHelper}. Каждая строка — отдельный запрос.`
    : "Сначала выберите тип запроса"

  const counterColor =
    parsed.invalid.length > 0 ? "error.main" : "text.secondary"

  return (
    <Stack spacing={1}>
      <TextFieldElement
        name={name}
        label="Запросы (по одному на строку)"
        required={required}
        disabled={disabled || !meta}
        multiline
        rows={12}
        placeholder={meta?.placeholder ?? ""}
        helperText={helperText}
        fullWidth
      />
      <Typography variant="caption" sx={{ color: counterColor }}>
        Строк: {parsed.total}, валидных: {parsed.payloads.length}
        {parsed.invalid.length > 0 && (
          <>
            {", невалидных: "}
            {parsed.invalid.length} (стр.{" "}
            {parsed.invalid.map((i) => i.line).join(", ")})
          </>
        )}
      </Typography>
    </Stack>
  )
}
